import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import LanguageSelector from '../common/LanguageSelector';

interface PaymentGateway {
  momo: {
    enabled: boolean;
    partnerCode?: string;
    accessKey?: string;
    secretKey?: string;
    environment?: 'development' | 'production';
  };
  vnpay: {
    enabled: boolean;
    tmnCode?: string;
    hashSecret?: string;
    environment?: 'development' | 'production';
  };
  zalopay: {
    enabled: boolean;
    appId?: string;
    key1?: string;
    key2?: string;
    environment?: 'development' | 'production';
  };
  isInherited?: boolean;
  hasOperatorSettings?: boolean;
}

interface OperatorSettingsForm {
  currency: 'USD' | 'VND';
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const OperatorSettings: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<OperatorSettingsForm>({
    currency: 'VND',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [paymentGateways, setPaymentGateways] = useState<PaymentGateway>({
    momo: {
      enabled: false
    },
    vnpay: {
      enabled: false
    },
    zalopay: {
      enabled: false
    },
    hasOperatorSettings: false
  });
  const [loading, setLoading] = useState({
    password: false,
    currency: false,
    gateways: false
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchPaymentGateways();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/operators/settings');
      setForm(prev => ({
        ...prev,
        currency: response.data.currency || 'VND'
      }));
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch settings');
    }
  };

  const fetchPaymentGateways = async () => {
    try {
      setLoading(prev => ({ ...prev, gateways: true }));
      const response = await api.get('/operators/settings/payment-gateways');
      
      // Check if operator has any gateway settings
      const hasAnyGatewaySettings = 
        (response.data.momo && (response.data.momo.partnerCode || response.data.momo.accessKey || response.data.momo.secretKey)) ||
        (response.data.vnpay && (response.data.vnpay.tmnCode || response.data.vnpay.hashSecret)) ||
        (response.data.zalopay && (response.data.zalopay.appId || response.data.zalopay.key1 || response.data.zalopay.key2));

      if (hasAnyGatewaySettings) {
        // If operator has their own settings, show all values
        setPaymentGateways({
          ...response.data,
          hasOperatorSettings: true,
          isInherited: false
        });
      } else {
        // If using inherited settings, just show enabled/disabled status
        setPaymentGateways({
          momo: {
            enabled: response.data.momo?.enabled || false,
            environment: response.data.momo?.environment || 'development'
          },
          vnpay: {
            enabled: response.data.vnpay?.enabled || false,
            environment: response.data.vnpay?.environment || 'development'
          },
          zalopay: {
            enabled: response.data.zalopay?.enabled || false,
            environment: response.data.zalopay?.environment || 'development'
          },
          isInherited: true,
          hasOperatorSettings: false
        });
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch payment gateways');
    } finally {
      setLoading(prev => ({ ...prev, gateways: false }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccessMessage('');
  };

  const handleGatewayChange = (gateway: 'momo' | 'vnpay' | 'zalopay', field: string, value: any) => {
    setPaymentGateways(prev => {
      // If this is the first change, mark as having operator settings
      const hasOperatorSettings = field === 'enabled' ? prev.hasOperatorSettings : true;
      
      return {
        ...prev,
        hasOperatorSettings,
        isInherited: false,
        [gateway]: {
          ...prev[gateway],
          [field]: value
        }
      };
    });
    setError('');
    setSuccessMessage('');
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setLoading(prev => ({ ...prev, password: true }));
      await api.put('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      
      setSuccessMessage('Password updated successfully');
      setForm(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(prev => ({ ...prev, password: false }));
    }
  };

  const handleCurrencyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    try {
      setLoading(prev => ({ ...prev, currency: true }));
      setError('');
      
      const response = await api.put('/operators/settings/currency', {
        currency: e.target.value
      });
      
      if (response.data && response.data.operator) {
        setForm(prev => ({
          ...prev,
          currency: response.data.operator.currency
        }));
        setSuccessMessage('Currency updated successfully');
      }
    } catch (error: any) {
      console.error('Currency update error:', error);
      setError(error.response?.data?.message || 'Failed to update currency');
      // Revert the form value on error
      fetchSettings();
    } finally {
      setLoading(prev => ({ ...prev, currency: false }));
    }
  };

  const handleGatewaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(prev => ({ ...prev, gateways: true }));
      await api.put('/operators/settings/payment-gateways', paymentGateways);
      setSuccessMessage('Payment gateways updated successfully');
      await fetchPaymentGateways(); // Refresh to get the latest state
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update payment gateways');
    } finally {
      setLoading(prev => ({ ...prev, gateways: false }));
    }
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">{t('settings.operatorSettings')}</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        {/* Language Settings */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.language')}</h3>
          <div className="max-w-md">
            <LanguageSelector />
          </div>
        </div>

        {/* Currency Settings */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">{t('settings.currency')}</h3>
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.currency')}
            </label>
            <select
              value={form.currency}
              onChange={handleCurrencyChange}
              disabled={loading.currency}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="USD">USD</option>
              <option value="VND">VND</option>
            </select>
          </div>
        </div>

        {/* Payment Gateways Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Payment Gateways</h3>
          {paymentGateways.isInherited && !paymentGateways.hasOperatorSettings && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4">
              {t('settings.paymentGatewayInheritMessage')}
            </div>
          )}
          <form onSubmit={handleGatewaySubmit} className="space-y-6">
            {/* Momo Gateway */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('payment.momo')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentGateways.momo.enabled}
                    onChange={(e) => handleGatewayChange('momo', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {paymentGateways.momo.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.partnerCode')}
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.momo.partnerCode || ''}
                      onChange={(e) => handleGatewayChange('momo', 'partnerCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.accessKey')}
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.momo.accessKey || ''}
                      onChange={(e) => handleGatewayChange('momo', 'accessKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.secretKey')}
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.momo.secretKey || ''}
                      onChange={(e) => handleGatewayChange('momo', 'secretKey', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.environment.label')}
                    </label>
                    <select
                      value={paymentGateways.momo.environment || 'development'}
                      onChange={(e) => handleGatewayChange('momo', 'environment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="development">{t('payment.environment.development')}</option>
                      <option value="production">{t('payment.environment.production')}</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* VNPay Gateway */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('payment.vnpay')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentGateways.vnpay.enabled}
                    onChange={(e) => handleGatewayChange('vnpay', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {paymentGateways.vnpay.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.tmnCode')}
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.vnpay.tmnCode || ''}
                      onChange={(e) => handleGatewayChange('vnpay', 'tmnCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.hashSecret')}
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.vnpay.hashSecret || ''}
                      onChange={(e) => handleGatewayChange('vnpay', 'hashSecret', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.environment.label')}
                    </label>
                    <select
                      value={paymentGateways.vnpay.environment || 'development'}
                      onChange={(e) => handleGatewayChange('vnpay', 'environment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="development">{t('payment.environment.development')}</option>
                      <option value="production">{t('payment.environment.production')}</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* ZaloPay Gateway */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-medium">{t('payment.zalopay')}</h4>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentGateways.zalopay.enabled}
                    onChange={(e) => handleGatewayChange('zalopay', 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {paymentGateways.zalopay.enabled && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.appId')}
                    </label>
                    <input
                      type="text"
                      value={paymentGateways.zalopay.appId || ''}
                      onChange={(e) => handleGatewayChange('zalopay', 'appId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.key1')}
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.zalopay.key1 || ''}
                      onChange={(e) => handleGatewayChange('zalopay', 'key1', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.key2')}
                    </label>
                    <input
                      type="password"
                      value={paymentGateways.zalopay.key2 || ''}
                      onChange={(e) => handleGatewayChange('zalopay', 'key2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={paymentGateways.isInherited && !paymentGateways.hasOperatorSettings ? t('settings.paymentGatewayOverrideHint') : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('payment.environment.label')}
                    </label>
                    <select
                      value={paymentGateways.zalopay.environment || 'development'}
                      onChange={(e) => handleGatewayChange('zalopay', 'environment', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="development">{t('payment.environment.development')}</option>
                      <option value="production">{t('payment.environment.production')}</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading.gateways}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.gateways ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.gateways ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>

        {/* Change Password Form */}
        <div className="border-t pt-8">
          <h3 className="text-lg font-semibold mb-4">{t('auth.password')}</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.currentPassword')}
              </label>
              <input
                type="password"
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.currentPassword')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.newPassword')}
              </label>
              <input
                type="password"
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.newPassword')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.confirmPassword')}
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
                placeholder={t('auth.confirmPassword')}
              />
            </div>
            <button
              type="submit"
              disabled={loading.password}
              className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                loading.password ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading.password ? t('common.loading') : t('common.save')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OperatorSettings; 